import { EGYPT_BOUNDS } from '../types'

const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'

let rowSeq = 1

export function createRow(partial = {}) {
  rowSeq += 1
  return {
    _id: `r${rowSeq}`,
    name: '',
    latitude: '',
    longitude: '',
    utm_east: '',
    utm_north: '',
    utm_zone: '',
    etm_east: '',
    etm_north: '',
    etm_zone: '',
    dms_latitude: '',
    dms_longitude: '',
    ...partial,
  }
}

export function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (value === null || value === undefined) return null
  let text = String(value).trim().replace(/,/g, '').replace(/\s/g, '')
  if (!text) return null
  text = text.replace(/[٠-٩]/g, digit => String(ARABIC_DIGITS.indexOf(digit)))
  text = text.replace(/[۰-۹]/g, digit => String(PERSIAN_DIGITS.indexOf(digit)))
  if (!/^[+-]?\d+(\.\d+)?$/.test(text)) return null
  const number = Number(text)
  return Number.isFinite(number) ? number : null
}

export function cleanText(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

export function coordinateColumns(system) {
  if (system === 'utm') {
    return [
      { key: 'name', label: 'الاسم', kind: 'text' },
      { key: 'utm_east', label: 'شرقي', kind: 'number' },
      { key: 'utm_north', label: 'شمالي', kind: 'number' },
      {
        key: 'utm_zone', label: 'المنطقة', kind: 'text', placeholder: '36N',
      },
    ]
  }
  if (system === 'etm') {
    return [
      { key: 'name', label: 'الاسم', kind: 'text' },
      { key: 'etm_east', label: 'شرقي', kind: 'number' },
      { key: 'etm_north', label: 'شمالي', kind: 'number' },
      { key: 'etm_zone', label: 'المنطقة', kind: 'text' },
    ]
  }
  if (system === 'dms') {
    return [
      { key: 'name', label: 'الاسم', kind: 'text' },
      {
        key: 'dms_latitude', label: 'خط العرض', kind: 'text', placeholder: '27°10\'48.0"N',
      },
      {
        key: 'dms_longitude', label: 'خط الطول', kind: 'text', placeholder: '31°11\'24.0"E',
      },
    ]
  }
  return [
    { key: 'name', label: 'الاسم', kind: 'text' },
    { key: 'latitude', label: 'خط العرض', kind: 'number' },
    { key: 'longitude', label: 'خط الطول', kind: 'number' },
  ]
}

export function isWgsSystem(system) {
  return !system || system === 'auto' || system === 'wgs84' || system === 'lat_lon'
}

function visibleKeys(system) {
  return coordinateColumns(system).map(column => column.key)
}

export function isRowBlank(row, system) {
  return visibleKeys(system).every(key => cleanText(row?.[key]) === '')
}

export function isRowComplete(row, system) {
  if (!row || isRowBlank(row, system)) return false
  if (system === 'utm') return toNumber(row.utm_east) !== null && toNumber(row.utm_north) !== null
  if (system === 'etm') {
    return toNumber(row.etm_east) !== null && toNumber(row.etm_north) !== null && Boolean(cleanText(row.etm_zone))
  }
  if (system === 'dms') return Boolean(cleanText(row.dms_latitude) && cleanText(row.dms_longitude))
  return toNumber(row.latitude) !== null && toNumber(row.longitude) !== null
}

export function buildCoordinate(row, system, fallbackZone) {
  const payload = {}
  const name = cleanText(row.name)
  if (name) payload.name = name

  const putNumber = (key, value) => {
    const number = toNumber(value)
    if (number !== null) payload[key] = number
  }
  const putText = (key, value) => {
    const text = cleanText(value)
    if (text) payload[key] = text
  }

  if (system === 'utm') {
    putNumber('utm_east', row.utm_east)
    putNumber('utm_north', row.utm_north)
    putText('utm_zone', row.utm_zone || fallbackZone || '36N')
    return payload
  }
  if (system === 'etm') {
    putNumber('etm_east', row.etm_east)
    putNumber('etm_north', row.etm_north)
    putText('etm_zone', row.etm_zone)
    return payload
  }
  if (system === 'dms') {
    putText('dms_latitude', row.dms_latitude)
    putText('dms_longitude', row.dms_longitude)
    return payload
  }
  if (system === 'wgs84' || system === 'lat_lon') {
    putNumber('latitude', row.latitude)
    putNumber('longitude', row.longitude)
    return payload
  }

  putNumber('latitude', row.latitude)
  putNumber('longitude', row.longitude)
  putNumber('utm_east', row.utm_east)
  putNumber('utm_north', row.utm_north)
  putText('utm_zone', row.utm_zone)
  putNumber('etm_east', row.etm_east)
  putNumber('etm_north', row.etm_north)
  putText('etm_zone', row.etm_zone)
  putText('dms_latitude', row.dms_latitude)
  putText('dms_longitude', row.dms_longitude)
  return payload
}

export function wgsIssues(row) {
  const latitude = toNumber(row.latitude)
  const longitude = toNumber(row.longitude)
  if (latitude === null && longitude === null) return { error: '', warning: '' }
  if (latitude === null || longitude === null) return { error: 'أدخل خط العرض وخط الطول معاً', warning: '' }
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return { error: 'خط العرض بين -90 و 90، وخط الطول بين -180 و 180', warning: '' }
  }
  const outside = latitude < EGYPT_BOUNDS.minLat
    || latitude > EGYPT_BOUNDS.maxLat
    || longitude < EGYPT_BOUNDS.minLng
    || longitude > EGYPT_BOUNDS.maxLng
  return {
    error: '',
    warning: outside ? 'النقطة خارج نطاق مصر (خط العرض 22–32، خط الطول 24–37)' : '',
  }
}

export function validateCoordinateRows(rows, system, mode, limits) {
  const errors = []
  const warnings = []
  const complete = [];
  (rows || []).forEach((row, index) => {
    if (isRowBlank(row, system)) return
    if (!isRowComplete(row, system)) {
      errors.push(`أكمل إحداثيات الصف ${index + 1} أو احذفه`)
      return
    }
    if (isWgsSystem(system)) {
      const issues = wgsIssues(row)
      if (issues.error) errors.push(`الصف ${index + 1}: ${issues.error}`)
      if (issues.warning) warnings.push(`الصف ${index + 1}: ${issues.warning}`)
    }
    complete.push(row)
  })

  const minimum = mode === 'polygon' ? 3 : 1
  if (complete.length < minimum) {
    errors.push(mode === 'polygon' ? 'أدخل 3 نقاط على الأقل لقطعة الأرض' : 'أدخل نقطة واحدة على الأقل')
  }
  const maximum = mode === 'polygon' ? limits?.max_polygon_vertices : limits?.max_points_per_request
  if (maximum && complete.length > maximum) {
    errors.push(mode === 'polygon'
      ? `الحد الأقصى لرؤوس القطعة هو ${maximum}`
      : `الحد الأقصى لعدد النقاط هو ${maximum}`)
  }
  return { errors, warnings, complete }
}

const HEADER_ALIASES = [
  { field: 'name', words: ['name', 'اسم', 'اسم النقطة', 'point', 'label'] },
  { field: 'latitude', words: ['latitude', 'lat', 'خط العرض', 'خط_العرض'] },
  { field: 'longitude', words: ['longitude', 'lng', 'lon', 'long', 'خط الطول', 'خط_الطول'] },
  { field: 'utm_east', words: ['utm_east', 'easting', 'east', 'شرقي'] },
  { field: 'utm_north', words: ['utm_north', 'northing', 'north', 'شمالي'] },
  { field: 'utm_zone', words: ['utm_zone', 'zone', 'منطقة'] },
  { field: 'etm_east', words: ['etm_east'] },
  { field: 'etm_north', words: ['etm_north'] },
  { field: 'etm_zone', words: ['etm_zone'] },
  { field: 'dms_latitude', words: ['dms_latitude', 'dms_lat'] },
  { field: 'dms_longitude', words: ['dms_longitude', 'dms_lng', 'dms_lon'] },
]

function normalizeHeader(value) {
  return cleanText(value).toLowerCase().replace(/[_-]+/g, ' ')
}

function headerField(cell, system) {
  const text = normalizeHeader(cell)
  if (!text) return ''
  const match = HEADER_ALIASES.find(item => item.words.some(word => normalizeHeader(word) === text))
  if (!match) return ''
  if (match.field === 'utm_east' || match.field === 'etm_east' || match.field === 'east') {
    if (system === 'etm') return 'etm_east'
    if (system === 'utm') return 'utm_east'
  }
  if (match.field === 'utm_north' || match.field === 'etm_north') {
    if (system === 'etm') return 'etm_north'
    if (system === 'utm') return 'utm_north'
  }
  if (match.field === 'utm_zone' || match.field === 'etm_zone') {
    if (system === 'etm') return 'etm_zone'
    if (system === 'utm') return 'utm_zone'
  }
  return match.field
}

function looksLikeHeader(cells) {
  const numeric = cells.filter(cell => toNumber(cell) !== null).length
  if (numeric >= 2) return false
  return cells.some(cell => /[A-Za-z\u0600-\u06FF]/.test(String(cell)))
}

function splitLine(line) {
  const delimiter = line.includes('\t') ? '\t' : ','
  return line.split(delimiter).map(cell => cell.trim().replace(/^"|"$/g, ''))
}

function looseRow(cells, system) {
  const row = createRow()
  if (system === 'dms') {
    const text = cells.map(cleanText).filter(Boolean)
    if (text.length >= 3 && toNumber(text[0]) === null) {
      const [name, dmsLatitude, dmsLongitude] = text
      row.name = name
      row.dms_latitude = dmsLatitude
      row.dms_longitude = dmsLongitude
      return row
    }
    if (text.length >= 2) {
      const [dmsLatitude, dmsLongitude] = text
      row.dms_latitude = dmsLatitude
      row.dms_longitude = dmsLongitude
      return row
    }
    return null
  }

  const numericIndexes = []
  cells.forEach((cell, index) => {
    if (toNumber(cell) !== null) numericIndexes.push(index)
  })
  if (numericIndexes.length < 2) return null
  const first = cells[numericIndexes[0]]
  const second = cells[numericIndexes[1]]
  if (system === 'utm') {
    row.utm_east = first
    row.utm_north = second
    const zone = cells.find(cell => /^\d{1,2}\s*[A-Za-z]$/.test(cleanText(cell)))
    if (zone) row.utm_zone = cleanText(zone).replace(/\s/g, '')
  } else if (system === 'etm') {
    row.etm_east = first
    row.etm_north = second
    const zone = cells.find((cell, index) => {
      if (numericIndexes.includes(index)) return false
      const text = cleanText(cell)
      return text && toNumber(text) === null && index !== 0
    })
    if (zone) row.etm_zone = cleanText(zone)
  } else {
    row.latitude = first
    row.longitude = second
  }
  const leading = cleanText(cells[0])
  if (leading && toNumber(leading) === null) row.name = leading
  return row
}

export function parsePastedTable(text, system) {
  const lines = String(text || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean)
  if (!lines.length) return []
  const matrix = lines.map(splitLine)
  const header = looksLikeHeader(matrix[0])
  const data = header ? matrix.slice(1) : matrix
  const fields = header ? matrix[0].map(cell => headerField(cell, system)) : []
  const hasMappedField = fields.some(Boolean)

  return data.map(cells => {
    if (header && hasMappedField) {
      const row = createRow()
      fields.forEach((field, index) => {
        if (!field) return
        row[field] = cells[index] || ''
      })
      return row
    }
    return looseRow(cells, system)
  }).filter(Boolean)
}

export function formatCoordInput(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return ''
  return String(Math.round(number * 1e8) / 1e8)
}

export function extensionOf(fileName) {
  const name = String(fileName || '').toLowerCase()
  const dot = name.lastIndexOf('.')
  if (dot < 0) return ''
  return name.slice(dot + 1)
}

export function validateFile(file, extensions, maxFileKb) {
  if (!file) return 'اختر ملفاً أولاً'
  const allowed = (extensions || []).map(item => String(item).replace(/^\./, '').toLowerCase())
  const extension = extensionOf(file.name)
  if (allowed.length && !allowed.includes(extension)) {
    return `صيغة الملف غير مدعومة. الصيغ المقبولة: ${allowed.map(item => `.${item}`).join('، ')}`
  }
  const maxBytes = Number(maxFileKb) * 1024
  if (Number.isFinite(maxBytes) && maxBytes > 0 && file.size > maxBytes) {
    const mb = Number(maxFileKb) / 1024
    const sizeText = mb >= 1 ? `${Number.isInteger(mb) ? mb : mb.toFixed(1)} ميجابايت` : `${maxFileKb} كيلوبايت`
    return `الحد الأقصى لحجم الملف ${sizeText}`
  }
  return ''
}
